<script lang="ts">
	import { browser } from '$app/env';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { PUBLIC_BACKEND } from '$app/env/public';
	import MarketingSeoHead from '$lib/components/marketing/MarketingSeoHead.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { localizedAbsoluteUrl, MARKETING_GIFT_OG_IMAGE } from '$lib/utils/marketing-seo';
	import { getLocale } from '@/paraglide/runtime';
	import { Button } from '$lib/components/ui/button';
	import * as Card from '$lib/components/ui/card';
	import { Checkbox } from '$lib/components/ui/checkbox';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import posthog from 'posthog-js';
	import { onMount } from 'svelte';

	let loading = $state(false);
	let error = $state<string | null>(null);
	let recipientName = $state('');
	let recipientEmail = $state('');
	let buyerEmail = $state('');
	let message = $state('');
	let acceptedTerms = $state(false);

	const giftOptions: Record<number, { title: string; description: string; image: string }> = {
		25: {
			title: 'Principiante de Muelle',
			description: 'Perfecto para quien aún no distingue babor de estribor.',
			image: '/img/gifts/Zarpar-regalo-25.avif'
		},
		50: {
			title: 'Grumete de Agua Dulce',
			description: 'Ideal para los que aún se marean en el pantalán.',
			image: '/img/gifts/zarpar-regalo-50.webp'
		},
		100: {
			title: 'Patrón de Domingo',
			description: 'Ya sabes hacer un as de guía (a veces).',
			image: '/img/gifts/Zarpar-regalo-100.avif'
		},
		200: {
			title: 'Lobo de Mar',
			description: 'La sal corre por tus venas más que la sangre.',
			image: '/img/gifts/Zarpar-regalo-200.webp'
		},
		500: {
			title: 'Poseidón',
			description: 'Los peces te piden permiso para nadar.',
			image: '/img/gifts/Zarpar-regalo-500.webp'
		}
	};

	const locale = $derived(getLocale());
	const checkoutSeoMeta = $derived.by(() => {
		void locale;
		return {
			title: m.gift_checkout_meta_title(),
			description: m.gift_checkout_meta_description(),
			image: MARKETING_GIFT_OG_IMAGE
		};
	});
	const giftLandingCanonicalUrl = $derived(localizedAbsoluteUrl('/regala-zarpar', locale));

	const amountParam = $derived(browser ? page.url.searchParams.get('amount') : null);
	const successParam = $derived(browser ? page.url.searchParams.get('success') : null);
	const promoCodeParam = $derived(browser ? page.url.searchParams.get('promo_code') : null);

	// Obtener el monto y estados derivados de los query params
	const amount = $derived.by(() => {
		if (!amountParam) return null;
		const parsedAmount = parseInt(amountParam, 10);
		return parsedAmount && giftOptions[parsedAmount] ? parsedAmount : null;
	});

	const success = $derived(successParam === 'true' && !!promoCodeParam);
	const promoCode = $derived(success ? promoCodeParam : null);

	// Track PostHog event when gift card purchase is successful
	onMount(() => {
		if (success && promoCode && amount) {
			posthog.capture('gift_card_purchased', {
				amount,
				promo_code: promoCode
			});
		}
	});

	const initialError = $derived.by(() => {
		if (!amountParam) return 'No se especificó un monto';
		if (!amount) return 'Monto de tarjeta regalo no válido';
		return null;
	});

	const selectedGift = $derived(amount ? giftOptions[amount] : null);

	async function handlePurchase() {
		if (!amount || !buyerEmail) {
			error = 'Por favor introduce tu email para continuar';
			return;
		}

		if (!acceptedTerms) {
			error = 'Debes aceptar los términos y condiciones de la tarjeta regalo';
			return;
		}

		loading = true;
		error = null;

		try {
			const response = await fetch(`${PUBLIC_BACKEND}/api/gift-card/checkout`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					amount,
					recipientName,
					recipientEmail,
					buyerEmail,
					message
				})
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.error || 'Error al crear la sesión de pago');
			}

			const data = await response.json();
			if (data.checkoutUrl) {
				window.location.href = data.checkoutUrl;
			} else {
				throw new Error('No se recibió la URL de checkout');
			}
		} catch (err) {
			error = err instanceof Error ? err.message : 'Error desconocido al procesar el pago';
			loading = false;
		}
	}
</script>

<MarketingSeoHead
	meta={checkoutSeoMeta}
	canonicalPath="/regala-zarpar"
	canonicalUrl={giftLandingCanonicalUrl}
	robots="noindex, follow"
	includeJsonLd={false}
/>

<div class="container mx-auto max-w-4xl py-10">
	{#if success && promoCode}
		<div class="text-center md:py-20">
			<div
				class="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100"
			>
				<svg
					class="h-10 w-10 text-green-600"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"
					></path>
				</svg>
			</div>
			<h1 class="mb-4 text-3xl font-bold">¡Tarjeta Regalo Comprada con Éxito!</h1>
			<p class="text-muted-foreground mb-8 text-lg">
				El código de promoción ha sido enviado por email al destinatario.
			</p>

			<Card.Root class="mx-auto max-w-md">
				<Card.Header>
					<Card.Title class="text-center">Código de Promoción</Card.Title>
				</Card.Header>
				<Card.Content class="space-y-4">
					<div class="border-primary bg-muted rounded-lg border-2 p-6 text-center">
						<code class="font-mono text-2xl font-bold tracking-wider">{promoCode}</code>
					</div>
					<p class="text-muted-foreground text-center text-sm">
						Este código ha sido enviado por email al destinatario. Puede usarlo al realizar una
						reserva.
					</p>
					<div class="rounded-md bg-blue-50 p-4 text-sm text-blue-900">
						<p class="mb-1 font-semibold">¿Tienes algún problema?</p>
						<p>
							Contacta con nosotros en
							<a href="mailto:info@zarpar.eu" class="underline hover:text-blue-700"
								>info@zarpar.eu</a
							>
						</p>
					</div>
				</Card.Content>
				<Card.Footer class="flex justify-center">
					<Button onclick={() => goto('/regala-zarpar')}>Comprar Otra Tarjeta</Button>
				</Card.Footer>
			</Card.Root>
		</div>
	{:else if initialError && !amount}
		<div class="py-20 text-center">
			<h1 class="text-destructive mb-4 text-2xl font-bold">Error</h1>
			<p class="text-muted-foreground mb-6">{initialError}</p>
			<Button onclick={() => goto('/regala-zarpar')}>Volver a seleccionar</Button>
		</div>
	{:else if selectedGift && amount}
		<div class="space-y-8">
			<div class="text-center">
				<h1 class="mb-2 text-4xl font-extrabold tracking-tight lg:text-5xl">
					Tarjeta Regalo de {amount} €
				</h1>
				<p class="text-muted-foreground text-lg">{selectedGift.title}</p>
			</div>

			<div class="grid gap-8 md:grid-cols-2">
				<!-- Imagen de la tarjeta -->
				<Card.Root class="overflow-hidden">
					<div class="aspect-4/3 w-full overflow-hidden">
						<img
							src={selectedGift.image}
							alt={selectedGift.title}
							class="h-full w-full object-cover"
						/>
					</div>
					<Card.Content class="pt-6">
						<p class="text-muted-foreground text-sm italic">"{selectedGift.description}"</p>
					</Card.Content>
				</Card.Root>

				<!-- Formulario -->
				<Card.Root>
					<Card.Header>
						<Card.Title>Detalles del Regalo</Card.Title>
						<Card.Description
							>Completa la información para enviar la tarjeta regalo</Card.Description
						>
					</Card.Header>
					<Card.Content class="space-y-4">
						<div class="space-y-2">
							<Label for="recipientName">Nombre del destinatario (opcional)</Label>
							<Input
								id="recipientName"
								bind:value={recipientName}
								placeholder="Nombre de quien recibirá el regalo"
								disabled={loading}
							/>
						</div>

						<div class="space-y-2">
							<Label for="recipientEmail">Email del destinatario (opcional)</Label>
							<Input
								id="recipientEmail"
								type="email"
								bind:value={recipientEmail}
								placeholder="email@ejemplo.com"
								disabled={loading}
							/>
						</div>

						<div class="space-y-2">
							<Label for="buyerEmail">Email del comprador *</Label>
							<Input
								id="buyerEmail"
								type="email"
								bind:value={buyerEmail}
								placeholder="tu@email.com"
								disabled={loading}
							/>
						</div>

						<div class="space-y-2">
							<Label for="message">Mensaje personalizado (opcional)</Label>
							<textarea
								id="message"
								bind:value={message}
								class="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
								placeholder="Escribe un mensaje personalizado..."
								disabled={loading}
							></textarea>
						</div>

						<div class="flex items-start space-x-2">
							<Checkbox id="terms" bind:checked={acceptedTerms} disabled={loading} />
							<Label
								for="terms"
								class="text-sm leading-none font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
							>
								Acepto los
								<a
									href="/terminos-tarjeta-regalo"
									target="_blank"
									class="text-primary hover:text-primary/80 underline"
									>Términos y Condiciones de la Tarjeta Regalo</a
								>
							</Label>
						</div>

						{#if error}
							<div class="bg-destructive/15 text-destructive rounded-md p-3 text-sm">
								{error}
							</div>
						{/if}

						<div class="pt-4">
							<Button
								class="w-full"
								size="lg"
								onclick={handlePurchase}
								disabled={loading || !buyerEmail || !acceptedTerms}
							>
								{#if loading}
									Procesando...
								{:else}
									Comprar Tarjeta Regalo de {amount} € 🎁
								{/if}
							</Button>
						</div>
					</Card.Content>
				</Card.Root>
			</div>

			<!-- Resumen de condiciones -->
			<Card.Root
				class="mt-8 border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20"
			>
				<Card.Header>
					<Card.Title>Condiciones de la Tarjeta Regalo</Card.Title>
				</Card.Header>
				<Card.Content>
					<ul class="text-muted-foreground space-y-2 text-sm">
						<li class="flex items-start">
							<span class="mr-2">•</span>
							<span>Válida por 6 meses desde la fecha de compra</span>
						</li>
						<li class="flex items-start">
							<span class="mr-2">•</span>
							<span>Disponible en cualquier alquiler o experiencia</span>
						</li>
						<li class="flex items-start">
							<span class="mr-2">•</span>
							<span>No reembolsable</span>
						</li>
						<li class="flex items-start">
							<span class="mr-2">•</span>
							<span
								>Si el importe de la reserva es menor que el valor de la tarjeta regalo, el saldo
								restante se perderá</span
							>
						</li>
					</ul>
					<div class="mt-4 border-t border-blue-200 pt-4 dark:border-blue-800">
						<a
							href="/terminos-tarjeta-regalo"
							target="_blank"
							class="text-primary hover:text-primary/80 text-sm font-medium underline"
							>Ver términos y condiciones completos</a
						>
					</div>
				</Card.Content>
			</Card.Root>
		</div>
	{/if}
</div>
